import i18n from '../../../i18n';
import { getCombinedOlId, updateLayerStyles } from '../../../services/theme';
import { THEME_GROUP_OTHER, INTERFACE_PANEL_TRIGGERS, LOCAL_STORAGE_KEYS, NEDGLOBE_SERVICES, OWS_TYPE } from '../../../constants';

export const actions = {
    async loadThemes({ dispatch, commit, state }, force) {
        if (!force && state.themes.length > 0) {
            return state.themes;
        }
        const loadedThemes = await dispatch('useApiData', {
            name: 'MobileGetThemeOverview'
        });
        // filter themes without a layer to display
        loadedThemes?.data
            .filter(({ themeLayers }) => themeLayers.length > 0)
            .forEach((themeData, index) => {
                commit('ADD_THEME', { themeData, active: false });
            });
        return loadedThemes;
    },
    async toggleTheme({ dispatch, getters }, { mapName, themeId, active }) {
        const theme = getters.getThemeById(themeId);
        if (active) {
            dispatch('trackRapportageEvent', { name: 'themeActivated', themeId, themeName: theme?.data?.name });
        }
        const toggledLayersPromisses = theme?.data?.themeLayers.map(layer => dispatch('toggleThemeLayer', { mapName, themeId, id: layer.id, active, toggledTheme: true }));
        return Promise.all(toggledLayersPromisses);
    },
    async toggleAllThemes({ dispatch, state }, { mapName, active }) {
        state.themes.map(theme => dispatch('toggleTheme', { mapName, themeId: theme.id, active }));
    },

    async setThemeLayerActive({ dispatch }, { mapName, layerData, id, customId }) {
        const getStusCodeFromMessage = (message) => {
            let statusCode = 500;

            if (message.includes('401')) {
                statusCode = 401;
            } else if (message.includes('403')) {
                statusCode = 403;
            } else if (message.includes('404')) {
                statusCode = 404;
            }
            return statusCode;
        };

        let error = '';

        if (layerData.data.serviceType === OWS_TYPE.OGCT) {
            layerData.checked = true;
            layerData.error = '';
        };
        // Check layer when activated for the first time
        // Don't check OGCT layers
        if (!layerData?.checked || layerData.error) {
            let result = null;

            try {
                result = await dispatch('useApiData', {
                    name: 'GetLayerExistsInCapabilities',
                    parameters: { Id: id }
                });
            } catch (errorMessage) {
                result = {
                    data: null,
                    status: errorMessage.error.status,
                    message: errorMessage.error.message,
                };

                if (!result.status) {
                    result.status = getStusCodeFromMessage(result.message);
                }
            }

            if (result.data === 'Ok') {
                dispatch('setLayerErrorState', { layerId: customId, error: '' });
                dispatch('setLayerCheckedState', { layerId: customId, checked: true });

                // Mark possible error as resolved
                await dispatch('useApiData', {
                    name: 'DeleteThemeLayerError',
                    parameters: { Id: layerData.data.id }
                });

                // when enabling a layer for the first time see if we need to get a outputformat.
                await dispatch('getLayerWfsOutputFormat', { layerData: layerData });
                await dispatch('getLayerWmsOutputFormat', { layerData: layerData });
            } else if (result.status === 401 ||
                       result.status === 403 ||
                       result.status === 404) {
                // unauthorized or forbidden or not found: this is not an themelayer error
                dispatch('setLayerUnAuthorizedState', { layerId: customId });
                dispatch('setLayerCheckedState', { layerId: customId, checked: false });
            } else {
                // theme layer error
                error = true;
                dispatch('setLayerErrorState', { layerId: customId, error: result.data });
                dispatch('setLayerCheckedState', { layerId: customId, checked: false });

                await dispatch('useApiData', {
                    name: 'CreateThemeLayerError',
                    data: { ThemeLayerId: layerData.data.id, ProblemMessage: result.data }
                });
            }
        }

        // We've checked the layer or it has been checked before. No problems found so let's activate
        if (!error) {
            if (layerData.data.serviceType != OWS_TYPE.OGCT) {
                await dispatch('setFilters', { layer: layerData });
            }
            await dispatch('addLayerToMap', { mapName, id: customId });
        }
    },

    async setThemeLayerInactive({ commit, dispatch }, { mapName, layerData, customId }) {
        await dispatch('removeLayerFromMap', { mapName, id: customId });

        // Remove current selection for this layer
        if (layerData?.data?.title) {
            commit('CLEAR_SELECTION_DATA', { title: layerData.data.title });
        }
    }
    ,

    async toggleThemeLayer({ dispatch, getters, commit }, { mapName, themeId, id, active, toggledTheme = false }) {

        const theme = getters.getThemeById(themeId);
        if (!theme) {
            return;
        }
        if (theme.themeLayersDataLoaderPromise === null) {
            await dispatch('loadTheme', { themeId, layerId: id, toggledTheme });
        }
        const customId = getCombinedOlId(themeId, id);

        await theme.themeLayersDataLoaderPromise;
        const layerData = getters.getLayerById(customId);

        if (active) {
            await dispatch('setThemeLayerActive', { mapName, layerData, id, customId });

        } else if (active === false) {
            await dispatch('setThemeLayerInactive', { mapName, layerData, customId });

        }
    },

    async getLayerWfsOutputFormat({ getters, dispatch }, { layerData }) {
        // No need to get format if it is not a WFS,
        // or if it is a feedback layer
        // or if the source is nedglobe services.
        if (layerData.data.serviceType === 'WFS' &&
            layerData.data.feedbackTypeId === 0 &&
            layerData.data.source.name != NEDGLOBE_SERVICES) {

            // Find out if we need to get source outputformat
            const sourceId = layerData.data.source.id;
            const source = getters.getSourceById(sourceId);

            // get from API when undefined
            if (source?.wfsOutputFormat === undefined) {

                const format = await dispatch('useApiData', {
                    name: 'GetLayerWfsOutputFormat',
                    parameters: { Id: layerData.data.id }
                });

                // Store the format for use in WFS layers
                dispatch('setSourceWfsOutPutFormat', { sourceId: sourceId, format: format.data });
            }
        }
    },

    async getLayerWmsOutputFormat({ getters, dispatch }, { layerData }) {
        // No need to get format if it is not a WFS,
        // or if it is a feedback layer
        // or if the source is nedglobe services.
        if (layerData.data.serviceType === 'WMS' &&
            layerData.data.feedbackTypeId === 0 &&
            layerData.data.source.name != NEDGLOBE_SERVICES) {

            // Find out if we need to get source outputformat
            const sourceId = layerData.data.source.id;
            const source = getters.getSourceById(sourceId);

            // get from API when undefined
            if (source?.wmsOutputFormat === undefined) {
                const format = await dispatch('useApiData', {
                    name: 'GetLayerWmsOutputFormat',
                    parameters: { Id: layerData.data.id }
                });

                // Store the format for use in WFS layers
                dispatch('setSourceWmsOutPutFormat', { sourceId: sourceId, format: format.data });
            }
        }
    },

    async loadTheme({ dispatch, commit, getters }, { themeId, layerId, toggledTheme }) {
        const theme = getters.getThemeById(themeId);
        const currentUserMap = getters.getActiveUserMap;
        if (theme.themeLayersDataLoaderPromise === null) {
            // eslint-disable-next-line no-async-promise-executor
            const themeLayersDataLoaderPromise = new Promise(async (resolve, reject) => {
                try {
                    const layerDataResult = await dispatch('useApiData', {
                        name: 'MobileGetTheme',
                        parameters: { id: themeId }
                    });
                    if (currentUserMap.content.sortedLayers) {
                        // update layerstyles with selected styles from usermap in layerDataResult.
                        updateLayerStyles(currentUserMap, layerDataResult, theme);
                    }

                    const layerPromises = await Promise.all(
                        layerDataResult.data.themeLayers
                            .map(
                                _layerData => dispatch('addLayer',
                                    {
                                        type: 'theme',
                                        layerData: { ..._layerData, customId: getCombinedOlId(themeId, _layerData.id), themeId },
                                        active: false,
                                        updateMap: toggledTheme || _layerData.id === layerId
                                    }
                                )
                            )
                    );
                    resolve(layerPromises);
                } catch (err) {
                    reject(err);
                }
            });
            commit('ADD_THEME_LAYERS_LOADER', { id: themeId, themeLayersDataLoaderPromise });
            return themeLayersDataLoaderPromise;
        }
        return theme.themeLayersDataLoaderPromise;
    },
    async getThemeFilters({ dispatch, getters }, { themeId }) {
        const theme = getters.getThemeById(themeId);
        if (!theme.data.ThemeLayers) {
            return theme.data;
        }
        return Promise.all(theme.data.themeLayers.map(({ id }) => dispatch('getThemeLayerAttributeFilters', { themeId: themeId, LayerId: id })));
    },
    async getThemeLayerAttributeFilters({ dispatch, getters }, { themeId, LayerId }) {
        let name = 'GetThemeLayerFilters';
        if (getters.isWeb) {
            name = 'WebGetThemeLayerFilters';
        }
        const filterRequest = await dispatch('useApiData', {
            name: name,
            parameters: {
                themeId: themeId,
                themeLayerId: LayerId
            }
        });
        return filterRequest?.data
            .map(filter => {
                // unselect checkbox if it has no default value
                if (!filter.value) {
                    filter.selected = false;
                }
                return { settings: filter };
            });
    },
    async getThemeGroups({ state, dispatch, commit }, { force }) {
        if (!force && state.themeGroups) {
            return state.themeGroups;
        }
        const themeGroupsResult = await dispatch('useApiData', {
            name: 'GetThemeGroups',
            parameters: state.userInfo.organizationId
        });

        const panelData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.UIELEMENTS));
        const themeGroups = themeGroupsResult.data?.map(result => ({ ...result }));
        const themeGroupsOtherLength = state.themes?.filter(theme => theme.themeGroupId === THEME_GROUP_OTHER).length;
        const hasThemeGroups = state.themes?.length !== themeGroupsOtherLength;

        const name = hasThemeGroups ? i18n.t('labelOther') : i18n.t('menuItemThemes');
        themeGroups.push({ id: THEME_GROUP_OTHER, name, order: themeGroups.length });

        const themeGroupsUpdated = themeGroups
            .map(group => {
                let active = false;
                if (hasThemeGroups) {
                    const localStorageThemeGroup = panelData?.panels?.layers?.components?.themegroups?.find(({ id }) => group.id === id);
                    active = localStorageThemeGroup?.active || active;
                } else {
                    active = state.uiElements[INTERFACE_PANEL_TRIGGERS.USERMAPS].components.layers.components.themes.active;
                }
                return {
                    ...group,
                    active
                };
            });

        commit('SET_THEMEGROUPS', themeGroupsUpdated);
        return themeGroupsUpdated;
    },
    async getLayerLegendUrl({ dispatch }, { themeLayer, styleName }) {
        const legend = await dispatch('useApiData', {
            name: 'GetLegendByStyle',
            parameters: { Id: themeLayer.id, Style: window.btoa(styleName) }
        });
        return legend.config.url;
    }
};
